import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ArticleCard } from '@/components/ArticleCard';
import { SEOHead } from '@/components/SEOHead';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { Loader2 } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryLabels = {
  actualites: 'Actualités',
  histoire: 'Histoire',
  culture: 'Culture',
  politique: 'Politique',
  sport: 'Sport'
};

export default function HomePage() {
  const [articles, setArticles] = useState([]);
  const [featuredArticles, setFeaturedArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const [articlesRes, featuredRes] = await Promise.all([
          axios.get(`${API_URL}/articles?limit=20`),
          axios.get(`${API_URL}/articles/featured?limit=3`)
        ]);
        
        setArticles(articlesRes.data);
        setFeaturedArticles(featuredRes.data);
      } catch (error) {
        console.error('Error fetching articles:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, []);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="homepage-loading">
        <Loader2 className="h-8 w-8 animate-spin text-red-800" />
      </div>
    );
  }

  const heroArticle = featuredArticles[0] || articles[0];
  const sideArticles = featuredArticles.length > 1 
    ? featuredArticles.slice(1, 3) 
    : articles.slice(1, 3);
  const gridArticles = articles.slice(featuredArticles.length > 0 ? 0 : 3, 12);

  // Group articles by category for sections
  const articlesByCategory = {};
  articles.forEach(article => {
    if (!articlesByCategory[article.category]) {
      articlesByCategory[article.category] = [];
    }
    articlesByCategory[article.category].push(article);
  });

  return (
    <>
      <SEOHead 
        title="VersaillesPlus | Actualités de Versailles et des Yvelines"
        description="VersaillesPlus - Votre quotidien d'information locale sur Versailles depuis 2007. Actualités, politique, culture, sport et justice des Yvelines."
      />
      <Header />
      <div className="bg-stone-100" data-testid="homepage">
      {/* Hero Section - La Une */}
      {heroArticle && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Main hero article */}
            <div className="lg:col-span-8">
              <ArticleCard article={heroArticle} variant="hero" />
            </div>
            
            {/* Side articles */}
            <div className="lg:col-span-4 space-y-6">
              {sideArticles.map((article) => (
                <ArticleCard key={article.id} article={article} variant="compact" />
              ))}
              
              {/* Ad placeholder or additional content */}
              <div className="bg-white border border-stone-200 p-6 text-center">
                <p className="font-inter text-xs text-stone-400 uppercase tracking-wider">
                  Publicité
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Divider */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <hr className="border-stone-300" />
      </div>

      {/* Articles Grid */}
      {gridArticles.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="font-playfair text-2xl font-bold text-stone-900 mb-8">
            Derniers articles
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-10">
            {gridArticles.map((article, index) => (
              <div 
                key={article.id} 
                className={`${index % 3 !== 2 ? 'lg:border-r lg:border-stone-200 lg:pr-8' : ''}`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <ArticleCard article={article} />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Category Sections */}
      {Object.entries(articlesByCategory).slice(0, 3).map(([category, catArticles]) => (
        catArticles.length > 0 && (
          <section key={category} className="bg-white border-t border-stone-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
              <div className="flex items-center justify-between mb-8">
                <h2 className="font-playfair text-2xl font-bold text-stone-900">
                  {categoryLabels[category] || category}
                </h2>
                <a 
                  href={`/categorie/${category}`}
                  className="font-inter text-sm text-red-800 hover:text-red-900 transition-colors duration-200"
                  data-testid={`see-all-${category}`}
                >
                  Voir tout →
                </a>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {catArticles.slice(0, 4).map((article) => (
                  <ArticleCard key={article.id} article={article} variant="compact" />
                ))}
              </div>
            </div>
          </section>
        )
      ))}

      {/* Empty state */}
      {articles.length === 0 && !loading && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <h2 className="font-playfair text-3xl font-bold text-stone-900 mb-4">
            Bienvenue sur VersaillesPlus
          </h2>
          <p className="font-lora text-stone-600 text-lg max-w-2xl mx-auto">
            Votre nouvelle source d'actualités locales. Les premiers articles arrivent bientôt. 
            Consultez l'espace administration pour lancer la génération de contenu.
          </p>
        </section>
      )}
    </div>
    <Footer />
    </>
  );
}
